<template>
	<AccessibleSection
		name="fab"
		focusFirstItem
		:tabOrder="[
			{
				css: '.fab-button',
				clickOnEnter: true,
				stayOnFocus: true,
				Escape: (vm, item, e) => {
					vm.focusOnElem(vm.root)
					return null
				},
				Enter: () => {
					return this.options
						.map((opt) => ({
							css: `.options-item.${opt.class}`,
							clickOnEnter: true,
							Escape: closeOptions,
						}))
						.concat({
							css: '.fab-button.expanded',
							clickOnEnter: true,
							Escape: closeOptions,
							Enter: closeOptions,
						})
				},
			},
		]"
	>
		<div
			class="fab-button inset-shadow-right"
			v-if="!store.config.embedded && !store.external_launch"
			:title="
				isExpanded
					? $t('chat_drawer_button_close_alt_text')
					: $t('chat_drawer_button_open_alt_text')
			"
			:class="{ expanded: isExpanded }"
			@click.stop="toggleOptions"
			tabindex="-1"
			role="button"
		>
			<PlusIcon />
		</div>

		<div class="options-overlay" :class="{ expanded: isExpanded }" />

		<div
			class="options-container inset-shadow-right"
			:class="{ expanded: isExpanded }"
			v-on-click-outside="closeOptions"
		>
			<div class="options-title" v-t="'chat_drawer_addbubble_title'" />

			<div
				class="options-item"
				v-for="option in options"
				:key="option.id"
				@click="option.onClick"
				:class="option.class"
				role="button"
			>
				<component :is="option.icon" class="options-icon" type="regular" />
				<span class="options-label" v-t="option.label" />
			</div>
		</div>
	</AccessibleSection>
</template>

<script>
import store, { canSendAnnouncements } from '@/store'
import { AccessibleSection } from '@/components/common/Accessible'
import {
	PlusIcon,
	CommentsIcon,
	UserGroupIcon,
	BullhornIcon,
	CalendarCheckIcon,
	LinkIcon,
} from '@/icons'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'

export default {
	name: 'FAB',
	components: { AccessibleSection, PlusIcon },
	data() {
		return {
			store,
			showOptions: false,
		}
	},
	computed: {
		isExpanded() {
			return this.showOptions
		},
		options() {
			const options = []
			if (store.checkOrgPermission('create_dm')) {
				options.push({
					icon: CommentsIcon,
					class: 'create-dm',
					label: 'chat_drawer_addbubble_directmessage',
					onClick: () => this.emitEvent('create_direct_message'),
				})
			}
			if (store.checkOrgPermission('create_group')) {
				options.push({
					icon: UserGroupIcon,
					class: 'create-group',
					label: 'chat_drawer_addbubble_create',
					onClick: () => this.emitEvent('create_group'),
				})
			}
			options.push({
				icon: LinkIcon,
				class: 'join-invite',
				label: 'groupinvites_join_title',
				onClick: () => this.emitEvent('enter_group_code'),
			})
			if (canSendAnnouncements()) {
				options.push({
					icon: BullhornIcon,
					class: 'send-announcement',
					label: 'chat_drawer_addbubble_announcement',
					onClick: () => this.emitEvent('send_announcement'),
				})
			}
			if (store.tasksEnabled()) {
				options.push({
					icon: CalendarCheckIcon,
					class: 'create-task',
					label: 'chat_drawer_addbubble_task',
					onClick: () => this.emitEvent('create_task'),
				})
			}
			return options
		},
	},
	methods: {
		openOptions() {
			this.showOptions = true
			this.escapeId = addEscapeListener(() => this.closeOptions())
		},
		closeOptions() {
			this.showOptions = false
			removeEscapeListener(this.escapeId)
		},
		toggleOptions() {
			if (this.showOptions) {
				this.closeOptions()
			} else {
				this.openOptions()
			}
		},
		emitEvent(eventName) {
			EventBus.$emit('omniboxAction', eventName)
			this.closeOptions()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$size: 50px;
$distance: 20px;
$options-height: 315px;
$z: 5;

.fab-button {
	height: $size;
	width: $size;
	z-index: $z + 2;
	transition: transform 0.3s ease-in-out;
	@include flex-center;
	background: var(--teal-300);
	box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.13);
	border-radius: 50%;
	overflow: hidden;
	font-size: 26px;
	cursor: pointer;
	color: var(--neutral-0);
	position: absolute;
	bottom: $distance;
	right: $distance;
	&.expanded {
		transform: rotate(45deg);
	}
}
.options-container {
	width: 100%;
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background: var(--neutral-0);
	padding: 16px 0;
	overflow: hidden;
	height: 0;
	opacity: 0;
	z-index: $z + 1;
	transition: all 0.3s ease-in-out;
	transition-property: height, opacity;
	border-top: 1px solid var(--neutral-40040);
	&.expanded {
		height: $options-height;
		opacity: 1;
	}
	.options-title {
		@include fontFamily(800);
		color: var(--neutral-800);
		margin: 0 0 8px 32px;
	}
	.options-item {
		@include flex;
		cursor: pointer;
		padding: 8px 32px;
		&:hover {
			background: var(--neutral-100);
		}
		.options-icon {
			font-size: 20px;
			margin-right: 16px;
		}
	}
}
.options-overlay {
	opacity: 0;
	height: 0;
	overflow: hidden;
	position: absolute;
	right: 0;
	left: 0;
	bottom: 0;
	transition: opacity 0.3s linear;
	z-index: $z;
	background: var(--neutral-0);
	&.expanded {
		height: auto;
		top: 0;
		opacity: 0.5;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.options-overlay {
		&.expanded {
			opacity: 0.8;
		}
	}
}
</style>
